import { createClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

if (!supabaseUrl || !supabaseKey) {
  throw new Error(
    "Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY in .env"
  );
}

const rawClient = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// ============================================================
// GROUP IMAGE CACHE
// ============================================================
type GroupImages = { logo: string | null; bg: string | null };

const groupCache = new Map<number, GroupImages>();
const inflight = new Map<number, Promise<GroupImages>>();

async function fetchGroupImages(vendcat: number | null): Promise<GroupImages> {
  if (vendcat == null) return { logo: null, bg: null };

  const cached = groupCache.get(vendcat);
  if (cached) return cached;

  const pending = inflight.get(vendcat);
  if (pending) return pending;

  const promise = (async () => {
    try {
      const { data, error } = await rawClient
        .from("vendimgcat")
        .select("logo, bg")
        .eq("id", vendcat)
        .maybeSingle();

      if (error || !data) {
        const empty: GroupImages = { logo: null, bg: null };
        groupCache.set(vendcat, empty);
        return empty;
      }

      const result: GroupImages = {
        logo: data.logo ?? null,
        bg: data.bg ?? null,
      };
      groupCache.set(vendcat, result);
      return result;
    } finally {
      inflight.delete(vendcat);
    }
  })();

  inflight.set(vendcat, promise);
  return promise;
}

// ============================================================
// FALLBACK APPLIER — fills empty profile_image / cover_image
// ============================================================
const isBlank = (v: unknown): boolean =>
  v == null || v === "" || v === "null" || v === "undefined";

async function applyFallback(row: any): Promise<any> {
  if (!row || typeof row !== "object") return row;

  const hasVendorShape =
    "profile_image" in row || "cover_image" in row || "vendcat" in row;

  if (!hasVendorShape) return row;

  const needsLogo = isBlank(row.profile_image);
  const needsBg = isBlank(row.cover_image);

  if (!needsLogo && !needsBg) return row;

  const group = await fetchGroupImages(row.vendcat ?? null);

  return {
    ...row,
    profile_image: isBlank(row.profile_image) ? group.logo : row.profile_image,
    cover_image: isBlank(row.cover_image) ? group.bg : row.cover_image,
  };
}

async function applyFallbackToData(data: any): Promise<any> {
  if (data == null) return data;
  if (Array.isArray(data)) {
    return Promise.all(data.map(applyFallback));
  }
  return applyFallback(data);
}

// ============================================================
// PROXY — intercept vendor_profiles SELECTs and inject fallback
// ============================================================
const READ_METHODS = new Set(["select", "single", "maybeSingle"]);

function wrapQueryForFallback(query: any, isVendorProfiles: boolean) {
  if (!isVendorProfiles) return query;

  return new Proxy(query, {
    get(q, prop, receiver) {
      const value = Reflect.get(q, prop, receiver);

      if (prop === "then") {
        return (onFulfilled: any, onRejected: any) =>
          (value.call(q) as Promise<any>)
            .then(async (res: any) => {
              if (res && !res.error) {
                res.data = await applyFallbackToData(res.data);
              }
              return onFulfilled ? onFulfilled(res) : res;
            })
            .catch(onRejected);
      }

      if (typeof value === "function") {
        return (...args: any[]) => {
          const next = value.apply(q, args);
          return wrapQueryForFallback(next, isVendorProfiles);
        };
      }

      return value;
    },
  });
}

export const supabase = new Proxy(rawClient, {
  get(target, prop, receiver) {
    if (prop !== "from") return Reflect.get(target, prop, receiver);

    return (table: string) => {
      const builder = target.from(table);
      const isVendorProfiles = table === "vendor_profiles";

      if (!isVendorProfiles) return builder;

      // vendor_profiles → reads get fallback, writes pass through untouched
      return new Proxy(builder, {
        get(b, p, recv) {
          const value = Reflect.get(b, p, recv);

          // Write operations → bypass fallback entirely
          if (
            p === "update" ||
            p === "insert" ||
            p === "delete" ||
            p === "upsert"
          ) {
            return typeof value === "function" ? value.bind(b) : value;
          }

          // Read chain → wrap so fallback is applied at await time
          if (typeof value === "function") {
            return (...args: any[]) => {
              const next = value.apply(b, args);
              return wrapQueryForFallback(next, true);
            };
          }

          return value;
        },
      });
    };
  },
});

// ============================================================
// OPTIONAL: manual helper if you ever need to bypass the proxy
// ============================================================
export const rawSupabase = rawClient;

// ============================================================
// OPTIONAL: clear cache (call if you edit vendimgcat at runtime)
// ============================================================
export function clearVendImgCatCache() {
  groupCache.clear();
  inflight.clear();
}